import {
  convertEventSelectToTarget,
  ModalBase,
  Select,
  STATUSPAGE,
} from '@repo/ui';
import { useFormik } from 'formik';
import { NumericDecimal } from 'presentation/app/components/inputs/numeric-decimal';
import { clearSelectedQuestion } from 'presentation/config/store/evaluation-criteria/questions/questions-slice';
import { FormEvent } from 'react';
import { useDispatch } from 'react-redux';
import { Col, Row, Form, Label, Input, Alert } from 'reactstrap';
import * as Yup from 'yup';

import { QUESTION_TYPE_OPTIONS } from '../../../common/question-types';
import { useQuestions } from '../../../hooks/useQuestions';

interface IModalQuestionsFormProps {
  id: string;
  isOpen: boolean;
  toggle: () => void;
  loadingQuestion: boolean;
}

export const ModalQuestionsForm = ({
  id,
  isOpen,
  toggle,
  loadingQuestion,
}: IModalQuestionsFormProps) => {
  const dispatch = useDispatch();
  const { requestUpdateQuestion, selectedQuestion, updateStatus } =
    useQuestions();

  const showTypeField = ['number', 'integer'].includes(selectedQuestion?.type);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      title: selectedQuestion ? selectedQuestion?.title : '',
      weight: selectedQuestion ? selectedQuestion?.weightage : '',
      maxScore: selectedQuestion ? selectedQuestion?.maxScore ?? '' : '',
      required: selectedQuestion?.required ? true : false,
      instructions: selectedQuestion ? selectedQuestion?.instruction : '',
      type:
        QUESTION_TYPE_OPTIONS.find(
          ({ value }) => value === selectedQuestion?.type,
        ) ?? null,
    },
    validationSchema: Yup.object({
      title: Yup.string().required(),
      maxScore: Yup.string().nullable(),
      weight: Yup.string().nullable(),
      required: Yup.boolean().nullable(),
      instructions: Yup.string().nullable(),
      type: Yup.object()
        .shape({
          value: Yup.string(),
          label: Yup.string(),
        })
        .nullable(),
    }),
    onSubmit: (values) => {
      const valuesToSumit = {
        id: selectedQuestion.id,
        criteriaId: id,
        title: values.title,
        required: values.required,
        weightage:
          values.weight === null ||
          values.weight === '' ||
          values.weight === '0'
            ? null
            : Number(values.weight),
        maxScore:
          values.maxScore === null || values.maxScore === ''
            ? null
            : Number(values.maxScore),
        instruction: values.instructions,
        // o tipo é enviado apenas quando usuário altera entre número decimal e inteiro
        ...(values.type?.value !== selectedQuestion?.type && {
          type: values.type?.value,
        }),
      };

      requestUpdateQuestion(valuesToSumit, toggle);
    },
  });

  return (
    <ModalBase.Wrapper
      loading={loadingQuestion}
      keyboard={false}
      isOpen={isOpen}
      toggle={() => {
        toggle();
      }}
      onClosed={() => {
        validation.resetForm();
        dispatch(clearSelectedQuestion());
      }}
    >
      <ModalBase.Header toggle={toggle} withBorderBottom>
        Editar Questão
      </ModalBase.Header>
      <Form
        onSubmit={(e: FormEvent<HTMLFormElement>) => {
          e.preventDefault();
          validation.handleSubmit(e);
          return false;
        }}
      >
        <ModalBase.Body>
          <Row className="g-3">
            <Col md={12}>
              <Label>Título</Label>
              <Input
                id="title"
                name="title"
                onBlur={validation.handleBlur}
                value={validation.values.title}
                onChange={validation.handleChange}
                invalid={
                  !!(validation.touched.title && validation.errors.title)
                }
              />
            </Col>

            {showTypeField && (
              <Col md={6}>
                <Label className="form-label" htmlFor="type">
                  Tipo
                </Label>
                <Select
                  id="type"
                  name="type"
                  placeholder="Selecione um tipo"
                  onBlur={validation.handleBlur}
                  value={validation?.values?.type}
                  options={QUESTION_TYPE_OPTIONS.filter((option) =>
                    ['number', 'integer'].includes(option.value),
                  )}
                  onChange={(e) => {
                    validation.handleChange(
                      convertEventSelectToTarget('type', e),
                    );
                  }}
                />
              </Col>
            )}

            <Col md={6}>
              <Label className="form-label">Peso</Label>
              <NumericDecimal
                id="weight"
                name="weight"
                allowNegative={false}
                onBlur={validation.handleBlur}
                value={validation.values.weight}
                onChange={validation.handleChange}
              />
            </Col>

            <Col md={12}>
              <Label className="form-label" htmlFor="maxScore">
                Nota Máxima
              </Label>
              <NumericDecimal
                id="maxScore"
                name="maxScore"
                allowNegative={false}
                onBlur={validation.handleBlur}
                value={validation.values.maxScore}
                onChange={validation.handleChange}
                isAllowed={(vals) => {
                  if (vals.floatValue === undefined) return true;
                  return vals.floatValue < 1000;
                }}
              />
            </Col>

            <Col
              xs={12}
              md={6}
              lg={6}
              style={{
                maxHeight: 80,
                display: 'grid',
                alignItems: 'end',
                justifyContent: 'start',
              }}
              className="mt-3"
            >
              <div className="form-check">
                <Input
                  id="required"
                  type="checkbox"
                  name="required"
                  className="form-check-input"
                  onBlur={validation.handleBlur}
                  onChange={validation.handleChange}
                  checked={validation.values.required}
                />
                <Label htmlFor="required" className="form-label">
                  Resposta Obrigatória
                </Label>
              </div>
            </Col>

            <Col xs={12} md={12} lg={12}>
              <Label htmlFor="instructions" className="form-label">
                Instrução
              </Label>
              <Input
                rows={4}
                id="instructions"
                type="textarea"
                name="instructions"
                className="form-control"
                onBlur={validation.handleBlur}
                value={validation.values?.instructions}
                onChange={validation.handleChange}
              />
            </Col>

            <Col xs={12} md={12} lg={12}>
              <Alert
                className="alert-icon"
                style={{
                  backgroundColor: '#FEF8E2',
                  borderColor: '#EBE7CA',
                  color: '#c9ab40',
                }}
              >
                <i className="ri-information-line me-2 fs-16"></i>
                <strong>Atenção!</strong> Quaisquer alterações realizadas não
                terão impacto nas avaliações anteriores dos estudantes
              </Alert>
            </Col>
          </Row>
        </ModalBase.Body>
        <ModalBase.Footer>
          <ModalBase.Actions
            loading={updateStatus === STATUSPAGE.LOADING}
            actionBtnType="submit"
            goBack={() => {
              toggle();
              validation.resetForm();
            }}
          />
        </ModalBase.Footer>
      </Form>
    </ModalBase.Wrapper>
  );
};
